import axios from 'axios'
import { globalConfig } from './common'

/**
 * 通用上传工具类
 * 提供文件上传、图片压缩、文件验证等通用功能
 */
class UploadUtils {
  /**
   * 创建axios实例用于文件上传
   * @param {string} baseURL - 基础URL
   * @returns {Object} axios实例
   */
  static createUploadInstance (baseURL = globalConfig.openApiURL) {
    return axios.create({
      baseURL,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  /**
   * 通用文件上传方法
   * @param {File} file - 文件对象
   * @param {string} uploadUrl - 上传接口地址
   * @param {Object} options - 上传选项
   * @returns {Promise<Object>} 上传结果
   */
  static async uploadFile (file, uploadUrl = 'api/file/upload', options = {}) {
    // if (!file) {
    //   throw new Error('请选择文件')
    // }
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)

    // 添加额外的表单数据
    if (options.formData) {
      Object.keys(options.formData).forEach(key => {
        formData.append(key, options.formData[key])
      })
    }

    const axiosInstance = this.createUploadInstance(options.baseURL)

    try {
      const res = await axiosInstance({
        method: 'POST',
        url: uploadUrl,
        data: formData
      })

      if (res.data.code !== 0) {
        throw new Error(res.data.message || '上传失败')
      }

      return res.data
    } catch (error) {
      console.error('文件上传失败:', error)
      throw new Error(error.message || '上传失败，请重试')
    }
  }

  /**
   * 验证文件类型
   * @param {File} file - 文件对象
   * @param {Array} allowedTypes - 允许的文件类型
   * @returns {boolean} 是否有效
   */
  static validateFileType (file, allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']) {
    if (!file) return false
    return allowedTypes.includes(file.type)
  }

  /**
   * 验证文件大小
   * @param {File} file - 文件对象
   * @param {number} maxSize - 最大文件大小（MB）
   * @returns {boolean} 是否有效
   */
  static validateFileSize (file, maxSize = 5) {
    if (!file) return false
    const fileSizeMB = file.size / (1024 * 1024)
    return fileSizeMB <= maxSize
  }

  /**
   * 压缩图片
   * @param {File} file - 文件对象
   * @param {Object} options - 压缩选项
   * @returns {Promise<File>} 压缩后的文件
   */
  static async compressImage (file, options = {}) {
    const {
      maxWidth = 1920,
      maxHeight = 1080,
      quality = 0.8
    } = options

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      img.onload = () => {
        // 计算压缩后的尺寸
        let { width, height } = img
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width *= ratio
          height *= ratio
        }

        canvas.width = width
        canvas.height = height

        // 绘制压缩后的图片
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            })
            resolve(compressedFile)
          },
          file.type,
          quality
        )
      }

      img.onerror = () => {
        reject(new Error('图片加载失败'))
      }
      console.log('图片压缩成功...')
      img.src = URL.createObjectURL(file)
    })
  }

  /**
   * 获取文件扩展名
   * @param {File} file - 文件对象
   * @returns {string} 文件扩展名
   */
  static getFileExtension (file) {
    if (!file || !file.name) return ''
    return file.name.split('.').pop().toLowerCase()
  }

  /**
   * 格式化文件大小
   * @param {number} bytes - 字节数
   * @returns {string} 格式化后的大小
   */
  static formatFileSize (bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * 创建文件预览URL
   * @param {File} file - 文件对象
   * @returns {string} 预览URL
   */
  static createPreviewUrl (file) {
    if (!file) return ''
    return URL.createObjectURL(file)
  }

  /**
   * 销毁文件预览URL
   * @param {string} url - 预览URL
   */
  static revokePreviewUrl (url) {
    if (url) {
      URL.revokeObjectURL(url)
    }
  }
}

export default UploadUtils



// WEBPACK FOOTER //
// ./src/utils/upload.js